import { useNavigate } from "@tanstack/react-router"
import { useGetOrderById } from "@/hooks/buyer/useGetOrderById"
import { useState } from "react"
import { z } from "zod"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

const inspectionMessageSchema = z
  .string()
  .trim()
  .min(1, "Inspection note is required")
  .min(10, "Inspection note must be at least 10 characters")
  .max(200, "Inspection note must not exceed 200 characters")
import { useAuthStore } from "@/store/auth.store"
import { getBuyerInvoiceUrl, getPurchaseOrderUrl, getPaymentReceiptUrl } from "@/lib/api/order.api"
import { CompanyDetailSidebar, ProductDetailSidebar, VariantDetailSidebar, ExternalSellerDetailSidebar, QuotationDetailSidebar } from "@/components/dashboard/shared/SourcingDetailSidebars"
import { QUANTITY_UNITS } from "@/components/dashboard/seller/pages/rfqs/RfqDetailsPage"
import { TargetItemView } from "@/components/dashboard/shared/TargetItemView"
import { CommercialDocumentCard } from "@/components/dashboard/shared/CommercialDocumentCard"
import { useAddInspection } from "@/hooks/buyer/useAddInspection"
import { useUpdateOrderZipaQuotation } from "@/hooks/buyer/useUpdateOrderZipaQuotation"
import { useGetTrackingDetails } from "@/hooks/useGetTrackingDetails"
import TrackingDetailsDisplay from "@/components/dashboard/admin/components/TrackingDetailsDisplay"
import {
  Loader2,
  AlertCircle,
  FileText,
  Calendar,
  Package,
  Activity,
  CheckCircle2,
  Clock,
  Play,
  DollarSign,
  Link2,
  ShieldCheck,
  Plus,
  Truck,
  Zap,
  Plane,
  Ship,
  ArrowRight,
  ExternalLink,
  Check,
  ChevronLeft,
  Building2,
} from "lucide-react"

function formatDate(isoStr: string) {
  if (!isoStr) return "N/A"
  return new Date(isoStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export function OrderDetailsPage({ orderId }: { orderId: string }) {
  const navigate = useNavigate()

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: "/orders" })
    }
  }

  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
  } = useGetOrderById(orderId, !!orderId)

  const selectedOrder = detailData?.order || (detailData && !(detailData as any).order && (detailData as any).id ? detailData : null)

  if (isDetailLoading) {
    return (
      <div className="relative flex flex-col items-center justify-center py-20 text-slate-400 gap-3 bg-white border border-[#be001e]/20 rounded-2xl shadow-sm max-w-5xl mx-auto font-sans text-left min-h-[300px]">
        <div className="absolute top-5 left-5">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-850"
            title="Back to Orders"
          >
            <ChevronLeft className="size-4" />
          </button>
        </div>
        <Loader2 className="size-8 animate-spin text-[#e6202f]" />
        <p className="text-sm font-medium text-slate-500">Fetching order details...</p>
      </div>
    )
  }

  if (isDetailError) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-850"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-[#e6202f]">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">{(detailError as Error)?.message || "Failed to load order details."}</p>
        </div>
      </div>
    )
  }

  if (!selectedOrder) {
    return (
      <div className="max-w-5xl mx-auto space-y-4 font-sans text-left">
        <div className="flex items-center gap-3">
          <button
            onClick={handleGoBack}
            className="flex items-center justify-center p-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer text-slate-850"
          >
            <ChevronLeft className="size-4" />
          </button>
          <span className="text-sm font-bold text-slate-700">Back to List</span>
        </div>
        <div className="flex items-center gap-3 rounded-2xl border border-yellow-100 bg-yellow-50 p-5 text-sm text-yellow-700">
          <AlertCircle className="size-5 shrink-0" />
          <p className="font-semibold">Order details not found.</p>
        </div>
      </div>
    )
  }

  return <OrderDetailView order={selectedOrder} onBack={handleGoBack} />
}

// ── Order Detail View ─────────────────────────────────────────────────────────
function OrderDetailView({ order, onBack }: { order: any; onBack: () => void }) {
  const navigate = useNavigate()
  const { accessToken } = useAuthStore()
  const q = order.quotation
  const charges = q?.charges || []
  const updateZipaQuotationMutation = useUpdateOrderZipaQuotation()
  const [selectedZipaRateId, setSelectedZipaRateId] = useState<string | null>(null)
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null)
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null)
  const [selectedExternalSeller, setSelectedExternalSeller] = useState<any | null>(null)
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [selectedQuotation, setSelectedQuotation] = useState<any | null>(null)
  const addInspectionMutation = useAddInspection(order.id)
  
  const [activeTab, setActiveTab] = useState<"overview" | "commercial" | "production" | "shipment" | "payments" | "inspections">("overview")

  const [showInspectionForm, setShowInspectionForm] = useState(false)
  const [inspectionMessage, setInspectionMessage] = useState("")
  const [inspectionMessageError, setInspectionMessageError] = useState("")

  const validateInspectionMessage = (value: string) => {
    const result = inspectionMessageSchema.safeParse(value)
    setInspectionMessageError(result.success ? "" : result.error.issues[0].message)
    return result.success
  }

  const { data: trackingDetails, isLoading: isTrackingLoading } = useGetTrackingDetails(
    order.shipment?.trackingUrl,
    order.shipment?.trackingId
  )

  const handleInspectionSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const isValid = validateInspectionMessage(inspectionMessage)
    if (!isValid) return

    addInspectionMutation.mutate(inspectionMessage.trim(), {
      onSuccess: () => {
        setInspectionMessage("")
        setInspectionMessageError("")
        setShowInspectionForm(false)
      }
    })
  }

  return (
    <div className="animate-in fade-in duration-200 text-left font-sans max-w-5xl mx-auto">
      <div className="group relative overflow-hidden bg-white border border-[#be001e]/20 hover:border-[#be001e]/30 rounded-2xl p-5 space-y-4 shadow-sm hover:shadow-[0_20px_50px_rgba(230,32,47,0.06)] transition-all duration-300">
        {/* Top Accent */}
        <div className="absolute top-0 left-0 right-0 h-[1px] group-hover:h-[3.5px] bg-gradient-to-r from-[#be001e] to-[#e6202f] rounded-t-2xl opacity-0 group-hover:opacity-100 transition-all duration-300" />

        {/* Header Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 rounded-lg border border-[#be001e]/20 bg-white hover:bg-[#be001e]/5 hover:text-[#be001e] hover:shadow-sm transition-all cursor-pointer p-1"
            >
              <ChevronLeft className="size-4" />
            </button>
              <span className="text-sm text-slate-700 font-medium ">Back to Orders</span>
          </div>

          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-[10px] font-bold border uppercase tracking-wider ${
              order.status === "CONFIRMED"
                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                : order.status === "PENDING"
                ? "bg-amber-50 text-amber-700 border-amber-100"
                : order.status === "CANCELLED"
                ? "bg-red-50 text-red-700 border-red-100"
                : "bg-slate-50 text-slate-700 border-slate-200"
            }`}>
              {order.status}
            </span>
          </div>
        </div>

        <div className="h-px bg-[#be001e]/10 w-full" />

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: Quotation info */}
          <div className="lg:col-span-3 space-y-4">
            {/* Tabs Header */}
            <div className="flex overflow-x-auto gap-2 pb-2 border-b border-slate-200 scrollbar-hide">
              {[
                { id: "overview", label: "Overview" },
                { id: "commercial", label: "Invoices" },
                { id: "production", label: "Production" },
                { id: "shipment", label: "Shipment" },
                { id: "payments", label: "Payments" },
                { id: "inspections", label: "Inspections" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-t-lg transition-colors border-b-2 cursor-pointer whitespace-nowrap ${
                    activeTab === tab.id 
                      ? "text-[#be001e] border-[#be001e] bg-[#be001e]/5" 
                      : "text-slate-500 border-transparent hover:text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="pt-2 space-y-4 min-h-[300px]">
            {activeTab === "commercial" && (
              <div className="space-y-6 animate-in fade-in duration-200">
                {/* 1. Purchase Orders Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                    <FileText className="size-4 text-[#e6202f]" /> Purchase Orders
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(() => {
                      const buyerPos = (order.purchaseOrders || []).filter((po: any) => po.type === "BUYER_TO_PLATFORM")
                      if (buyerPos.length > 0) {
                        return buyerPos.map((po: any) => {
                          const title = "Purchase Order"
                          const fileName = po.documentUrl ? po.documentUrl.slice(po.documentUrl.lastIndexOf('/') + 1) : `PurchaseOrder_${po.id.slice(0, 8)}.pdf`
                          const statusType = po.status === "ACCEPTED" ? "success" : po.status === "PENDING" ? "warning" : "error"
                          return (
                            <CommercialDocumentCard
                              key={po.id}
                              title={title}
                              fileName={fileName}
                              status={po.status}
                              statusType={statusType}
                              date={formatDate(po.createdAt)}
                              onGetSignedUrl={() => getPurchaseOrderUrl(order.id, po.id, accessToken || "")}
                            />
                          )
                        })
                      }
                      return (
                        <CommercialDocumentCard
                          title="Purchase Order"
                          fileName="PurchaseOrder.pdf"
                          status="Pending Generation"
                          statusType="default"
                        />
                      )
                    })()}
                  </div>
                </div>

                {/* 2. Invoices Section */}
                {order.invoiceUrl && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-sans">
                      <FileText className="size-4 text-[#e6202f]" /> Commercial Invoices
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Buyer Commercial Invoice */}
                      {order.invoiceUrl && (
                        <CommercialDocumentCard
                          title="Buyer Commercial Invoice"
                          fileName={order.invoiceUrl.slice(order.invoiceUrl.lastIndexOf('/') + 1)}
                          status="Issued"
                          statusType="success"
                          date={formatDate(order.createdAt)}
                          onGetSignedUrl={() => getBuyerInvoiceUrl(order.id, accessToken || "")}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "overview" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {/* Company Name */}
                {order?.company?.legalName && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Building2 className="size-3.5 text-[#e6202f]" /> Company Name
                    </h4>
                    <div 
                      onClick={() => setSelectedCompany(order.company)}
                      className="group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/30 bg-slate-50/40 hover:bg-slate-50 p-3 shadow-sm transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-slate-700 font-medium hover:text-[#be001e] hover:underline text-xs tracking-tight font-sans">
                          {order.company.legalName}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Linked Quotation */}
                {q && (
                  <div className="space-y-1.5">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <FileText className="size-3.5 text-[#e6202f]" /> Linked Quotation
                    </h4>
                    <div 
                      onClick={() => setSelectedQuotation(q)}
                      className="group/item flex items-center justify-between gap-4 rounded-xl border border-[#be001e]/15 hover:border-[#be001e]/30 bg-slate-50/40 hover:bg-slate-50 p-3 shadow-sm transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-extrabold text-[#be001e] hover:underline text-xs tracking-tight font-mono">
                          {q.message}
                        </span>
                      </div>
                      <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                        q.status === "APPROVED"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                          : q.status === "PENDING"
                          ? "bg-amber-50 text-amber-700 border-amber-100"
                          : "bg-red-50 text-red-700 border-red-100"
                      }`}>
                        {q.status}
                      </span>
                    </div>
                  </div>
                )}

            {/* Sourcing Target  */}
            {(() => {
              const sellerToAdminQuotation = q?.sellerToAdminQuotation
              const adminToSellerRfq = sellerToAdminQuotation?.adminToSellerRfq
              const target = adminToSellerRfq?.targets?.[0]

              if (!target?.company && !target?.product) return null

              return (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Package className="size-3.5 text-[#e6202f]" /> Sourcing Target
                  </h4>
                      <TargetItemView
                        target={target}
                        onProductClick={(p) => setSelectedProductId(p.id)}
                        onCompanyClick={(c) => setSelectedCompany(c)}
                        onVariantClick={(v) => setSelectedVariantId(v.id)}
                        showSpecsGrid
                      />
                </div>
              )
            })()}



            {/* Selected Logistics Rates (Zipaworld) */}
            {order.zipaWorldQuotation ? (
              <div className="space-y-3 pt-2">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <Zap className="size-3.5 text-amber-500 fill-amber-500/20" /> Selected Logistics Rate (Zipaworld)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(() => {
                    const zq = order.zipaWorldQuotation
                    const isAir = zq.shipmentMode?.toUpperCase() === "AIR"
                    return (
                      <div className="relative overflow-hidden group/zq flex flex-col justify-between rounded-xl border border-slate-200/80 bg-white p-4.5 space-y-4">
                        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-lg border ${isAir ? "bg-sky-50 border-sky-100 text-sky-600" : "bg-blue-50 border-blue-100 text-blue-600"}`}>
                              {isAir ? <Plane className="size-4" /> : <Ship className="size-4" />}
                            </div>
                            <div>
                              <h5 className="font-extrabold text-slate-800 text-[13px] leading-tight">{zq.serviceName || "Standard"}</h5>
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">{zq.quotationType?.replace(/_/g, " ") || "LOGISTICS RATE"}</p>
                            </div>
                          </div>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider text-slate-500 bg-slate-100 border-slate-200/50">{zq.transitTime || "N/A"}</span>
                        </div>
                        <div className="grid grid-cols-5 items-center gap-2 text-center py-1">
                          <div className="col-span-2 text-left min-w-0">
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">ORIGIN</span>
                            <span className="font-bold text-slate-700 text-xs truncate block">{zq.origin || "N/A"}</span>
                          </div>
                          <div className="col-span-1 flex flex-col items-center justify-center">
                            <ArrowRight className="size-3 text-slate-400" />
                          </div>
                          <div className="col-span-2 text-right min-w-0">
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">DESTINATION</span>
                            <span className="font-bold text-slate-700 text-xs truncate block">{zq.destination || "N/A"}</span>
                          </div>
                        </div>
                        <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3.5 text-xs font-semibold text-slate-600 space-y-2">
                          <div className="flex justify-between items-center text-[11.5px]">
                            <span className="text-slate-400">Freight Amount:</span>
                            <span className="font-mono text-slate-700">{zq.freightAmount || "N/A"}</span>
                          </div>
                          <div className="flex justify-between items-center text-[11.5px]">
                            <span className="text-slate-400">GST:</span>
                            <span className="font-mono text-slate-700">{zq.gst || "N/A"}</span>
                          </div>
                          <div className="h-px bg-slate-200/60 my-1" />
                          <div className="flex justify-between items-center text-xs font-bold">
                            <span className="text-slate-800">Total Amount:</span>
                            <span className="text-[#e6202f] font-mono font-black">{zq.totalAmount || "N/A"}</span>
                          </div>
                        </div>
                        {zq.viewRates && (
                          <div className="pt-1">
                            <a href={zq.viewRates} target="_blank" rel="noopener noreferrer" className="w-full h-9 border border-[#be001e]/20 text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/40 rounded-lg flex items-center justify-center gap-1.5 text-xs font-bold transition-all cursor-pointer shadow-sm">
                              <ExternalLink className="size-3.5" /> View Rates Details
                            </a>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              </div>
            ) : (
              q?.zipaQuotations && q.zipaQuotations.length > 0 && (
                <div className="space-y-3 pt-2">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Zap className="size-3.5 text-amber-500 fill-amber-500/20" /> Select Preferred Logistics Rate (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {q.zipaQuotations.map((zq: any) => {
                      const isAir = zq.shipmentMode?.toUpperCase() === "AIR"
                      const rateId = zq.id || zq.rateId
                      const isSelected = selectedZipaRateId === rateId
                      return (
                        <div
                          key={rateId}
                          onClick={() => setSelectedZipaRateId(prev => prev === rateId ? null : rateId)}
                          className={`relative overflow-hidden group/zq flex flex-col justify-between rounded-xl border transition-all duration-300 p-4.5 space-y-4 cursor-pointer ${isSelected ? "border-[#be001e] bg-[#be001e]/5 ring-2 ring-[#be001e]/10 shadow-sm" : "border-slate-200/80 bg-white hover:border-[#be001e]/30"}`}
                        >
                          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-lg border ${isSelected ? "bg-[#be001e]/10 border-[#be001e]/20 text-[#be001e]" : isAir ? "bg-sky-50 border-sky-100 text-sky-600" : "bg-blue-50 border-blue-100 text-blue-600"}`}>
                                {isAir ? <Plane className="size-4" /> : <Ship className="size-4" />}
                              </div>
                              <div>
                                <h5 className="font-extrabold text-slate-800 text-[13px] leading-tight flex items-center gap-1.5">
                                  {zq.serviceName || "Standard"}
                                  {isSelected && <span className="bg-[#be001e] text-white rounded-full p-0.5 inline-flex items-center justify-center size-3.5"><Check className="size-2.5" /></span>}
                                </h5>
                                <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">{zq.quotationType?.replace(/_/g, " ") || "LOGISTICS RATE"}</p>
                              </div>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${isSelected ? "bg-[#be001e]/15 text-[#be001e] border-[#be001e]/20 font-extrabold" : "text-slate-500 bg-slate-100 border-slate-200/50"}`}>
                              {zq.transitTime || "N/A"}
                            </span>
                          </div>
                          <div className="grid grid-cols-5 items-center gap-2 text-center py-1">
                            <div className="col-span-2 text-left min-w-0">
                              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">ORIGIN</span>
                              <span className="font-bold text-slate-700 text-xs truncate block">{zq.origin || "N/A"}</span>
                            </div>
                            <div className="col-span-1 flex flex-col items-center justify-center"><ArrowRight className="size-3 text-slate-400" /></div>
                            <div className="col-span-2 text-right min-w-0">
                              <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">DESTINATION</span>
                              <span className="font-bold text-slate-700 text-xs truncate block">{zq.destination || "N/A"}</span>
                            </div>
                          </div>
                          <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-3.5 text-xs font-semibold text-slate-600 space-y-2">
                            <div className="flex justify-between items-center text-[11.5px]"><span className="text-slate-400">Freight Amount:</span><span className="font-mono text-slate-700">{zq.freightAmount || "N/A"}</span></div>
                            <div className="flex justify-between items-center text-[11.5px]"><span className="text-slate-400">GST:</span><span className="font-mono text-slate-700">{zq.gst || "N/A"}</span></div>
                            <div className="h-px bg-slate-200/60 my-1" />
                            <div className="flex justify-between items-center text-xs font-bold"><span className="text-slate-800">Total Amount:</span><span className="text-[#e6202f] font-mono font-black">{zq.totalAmount || "N/A"}</span></div>
                          </div>
                          {zq.viewRates && (
                            <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                              <a href={zq.viewRates} target="_blank" rel="noopener noreferrer" className="w-full h-9 border border-[#be001e]/20 text-[#be001e] hover:bg-[#be001e]/5 hover:border-[#be001e]/40 rounded-lg flex items-center justify-center gap-1.5 text-xs font-bold transition-all cursor-pointer shadow-sm">
                                <ExternalLink className="size-3.5" /> View Rates Details
                              </a>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={() => {
                        if (!selectedZipaRateId) {
                          toast.warning("Please choose a shipment rate first")
                          return
                        }
                        updateZipaQuotationMutation.mutate(
                          { orderId: order.id, zipaWorldQuotationId: selectedZipaRateId },
                          { onSuccess: () => setSelectedZipaRateId(null) }
                        )
                      }}
                      disabled={updateZipaQuotationMutation.isPending}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#be001e]/90 text-white text-xs font-bold transition-all cursor-pointer shadow-sm",
                        !selectedZipaRateId && "opacity-90 cursor-not-allowed"
                      )}
                    >
                      {updateZipaQuotationMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Zap className="size-3.5" />}
                      {updateZipaQuotationMutation.isPending ? "Booking..." : "Book Selected Logistics Rate"}
                    </button>
                  </div>
                </div>
              )
            )}

            {/* Charges Breakdown */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1 font-sans">
                  <Package className="size-3.5 text-[#e6202f]" /> Charges Breakdown
                </h4>
              </div>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-3">
                {!q || !charges || charges.length === 0 ? (
                  <div className="p-8 text-center text-slate-400 italic text-xs">No charges specified</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                      <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                        <tr>
                          <th className="p-3 min-w-[160px]">Description</th>
                          <th className="p-3 text-center w-32">Unit Price</th>
                          <th className="p-3 text-center w-24">Quantity</th>
                          <th className="p-3 text-center w-24">Unit</th>
                          <th className="p-3 text-center w-28">Discount (%)</th>
                          <th className="p-3 text-center w-24">Tax (%)</th>
                          <th className="p-3 text-center w-24">Tax Type</th>
                          <th className="p-3 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {charges.map((charge: any) => {
                          const isAdvanced = charge.unitPrice !== undefined
                          const price = isAdvanced ? Number(charge.unitPrice || 0) : Number(charge.amount || 0)
                          const qty = isAdvanced ? ((charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "") ? Number(charge.quantity) : 1) : 1
                          const disc = isAdvanced ? Number(charge.discount || 0) : 0
                          const tax = isAdvanced ? Number(charge.tax || 0) : 0
                          const base = price * qty
                          const sub = base - (base * disc / 100)
                          const lineTotal = sub + (sub * tax / 100)
                          const unitRaw = charge.measureUnit || charge.unit || charge.quantityUnit
                          const unitDisplay = isAdvanced && unitRaw ? (QUANTITY_UNITS.find((u) => u.code.toLowerCase() === String(unitRaw).toLowerCase() || u.name.toLowerCase() === String(unitRaw).toLowerCase())?.code || unitRaw) : "-"
                          return (
                            <tr key={charge.id || charge.description} className="hover:bg-slate-50/50 transition-colors">
                              <td className="p-3 text-slate-800 font-semibold">
                                {charge.productVariantId ? (
                                  <button type="button" onClick={() => setSelectedVariantId(charge.productVariantId)} className="text-[#be001e] hover:text-[#e6202f] hover:underline text-left cursor-pointer transition-colors">
                                    {charge.description || "-"}
                                  </button>
                                ) : (charge.description || "-")}
                              </td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced && charge.unitPrice !== null && charge.unitPrice !== undefined && charge.unitPrice !== "" ? charge.unitPrice : "-"}</td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced && charge.quantity !== null && charge.quantity !== undefined && charge.quantity !== "" ? charge.quantity : "-"}</td>
                              <td className="p-3 text-center text-slate-600">{unitDisplay}</td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.discount || 0) : "-"}</td>
                              <td className="p-3 text-center text-slate-600">{isAdvanced ? (charge.tax || 0) : "-"}</td>
                              <td className="p-3 text-center text-slate-600">
                                {isAdvanced && charge.taxType ? <span className="text-[9px] font-bold bg-slate-100 text-slate-500 px-1 py-0.5 rounded uppercase border border-slate-200">{charge.taxType}</span> : "-"}
                              </td>
                              <td className="p-3 text-right text-slate-900 font-bold font-mono">{lineTotal.toFixed(2)}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {q && (() => {
                const computedChargesTotal = charges.reduce((acc: number, c: any) => {
                  const isAdv = c.unitPrice !== undefined
                  const p = isAdv ? Number(c.unitPrice || 0) : Number(c.amount || 0)
                  const qVal = isAdv ? ((c.quantity !== null && c.quantity !== undefined && c.quantity !== "") ? Number(c.quantity) : 1) : 1
                  const d = isAdv ? Number(c.discount || 0) : 0
                  const t = isAdv ? Number(c.tax || 0) : 0
                  const b = p * qVal
                  const s = b - (b * d / 100)
                  return acc + s + (s * t / 100)
                }, 0)
                const grandTotal = (order.totalAmount !== undefined && order.totalAmount !== null)
                  ? Number(order.totalAmount)
                  : computedChargesTotal

                return (
                  <div className="flex justify-end p-3 bg-white border border-slate-200 rounded-xl mt-3 shadow-sm">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-3 self-center">Grand Total:</span>
                    <span className="text-[12px] font-bold text-[#be001e] font-mono">{q.currencyCode} {grandTotal.toFixed(2)}</span>
                  </div>
                )
              })()}
            </div>
              </div>
            )}

            {activeTab === "production" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {(() => {
                  const stages = order.productionStages || []
                  if (stages.length === 0) return <p className="text-xs text-slate-400 italic">No production stages logged yet.</p>
                  return (
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <Activity className="size-3.5 text-[#e6202f]" /> Production Stages
                      </h4>
                      <div className="space-y-3">
                        {stages.map((stage: any) => (
                          <div key={stage.id} className="flex flex-col sm:flex-row justify-between sm:items-center bg-slate-50/50 border border-slate-100 rounded-xl p-4 gap-3">
                            <div className="space-y-1 text-left">
                              <h5 className="text-xs font-bold text-slate-800">{stage.name}</h5>
                              {stage.description && <p className="text-[11px] text-slate-500 leading-normal">{stage.description}</p>}
                              <span className="text-[9px] text-slate-400 block">Added: {formatDate(stage.createdAt)}</span>
                            </div>
                            <div className="flex items-center gap-2 self-end sm:self-auto">
                              <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${stage.status === "COMPLETED" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : stage.status === "IN_PROGRESS" ? "bg-sky-50 text-sky-700 border-sky-100" : "bg-amber-50 text-amber-700 border-amber-100"}`}>
                                {stage.status === "COMPLETED" && <CheckCircle2 className="size-2.5" />}
                                {stage.status === "IN_PROGRESS" && <Play className="size-2.5 animate-pulse" />}
                                {stage.status === "PENDING" && <Clock className="size-2.5" />}
                                {stage.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {activeTab === "payments" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                {(() => {
                  const payments = (order.payments || (order.payment ? [order.payment] : [])).filter((p: any) => p.type === "BUYER_TO_ADMIN")
                  if (payments.length === 0) return <p className="text-xs text-slate-400 italic">No payments logged yet.</p>
                  return (
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <DollarSign className="size-3.5 text-[#e6202f]" /> Payments
                      </h4>
                      <div className="space-y-2">
                        {payments.map((payment: any) => (
                          <div key={payment.id} className="flex flex-col sm:flex-row justify-between sm:items-center bg-slate-50/50 border border-slate-100 rounded-xl p-4 gap-3 text-xs">
                            <div className="space-y-1 text-left">
                              <div className="flex items-center gap-2">
                                <span className={`font-bold text-[10px] tracking-wide uppercase px-1.5 py-0.5 rounded border ${payment.type === "BUYER_TO_ADMIN" ? "bg-blue-50 text-blue-700 border-blue-100" : "bg-indigo-50 text-indigo-700 border-indigo-100"}`}>
                                  {payment.type.replace(/_/g, " ")}
                                </span>
                                <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${payment.status === "COMPLETED" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : payment.status === "PENDING" ? "bg-amber-50 text-amber-700 border-amber-100" : payment.status === "FAILED" ? "bg-red-50 text-red-700 border-red-100" : "bg-slate-50 text-slate-700 border-slate-200"}`}>
                                  {payment.status}
                                </span>
                              </div>
                              {payment.referenceId && <div className="flex items-center gap-1 text-slate-500 font-medium"><span className="font-semibold text-slate-400">Ref ID:</span><span className="font-mono">{payment.referenceId}</span></div>}
                              {payment.referenceUrl && (
                                <div className="flex items-center gap-1 text-[#e6202f]">
                                  <Link2 className="size-3" />
                                  <button
                                    onClick={async () => {
                                      try {
                                        const res = await getPaymentReceiptUrl(order.id, payment.id, accessToken || "")
                                        if (res?.url) {
                                          window.open(res.url, "_blank", "noopener,noreferrer")
                                        }
                                      } catch (error) {
                                        console.error("Failed to get payment receipt URL:", error)
                                        toast.error("Failed to fetch payment receipt document")
                                      }
                                    }}
                                    className="hover:underline font-medium text-left cursor-pointer"
                                  >
                                    View Payment Receipt
                                  </button>
                                </div>
                              )}
                              <span className="text-[9px] text-slate-400 block">Logged: {formatDate(payment.createdAt)}</span>
                            </div>
                            <div className="text-right self-end sm:self-auto">
                              <span className="text-sm font-black text-slate-900 font-mono">{q?.currencyCode ?? "—"} {payment.amount?.toLocaleString()}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {activeTab === "shipment" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="space-y-3 text-left">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                    <Truck className="size-3.5 text-[#e6202f]" /> Shipment & Tracking
                  </h4>
                  {order.shipment ? (
                    <div className="space-y-3 bg-slate-50/40 border border-slate-100 rounded-xl p-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5 text-xs pb-3 border-b border-slate-200/50">
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Carrier Provider</span>
                          <span className="font-extrabold text-slate-800 uppercase">{order.shipment.provider}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Tracking Number</span>
                          <span className="font-mono font-bold text-slate-800">{order.shipment.trackingId}</span>
                        </div>
                        <div>
                          <span className="text-slate-400 font-semibold block mb-0.5">Tracking Endpoint</span>
                          <a href={order.shipment.trackingUrl} target="_blank" rel="noopener noreferrer" className="text-[#e6202f] hover:underline font-mono text-[10.5px] truncate block" title={order.shipment.trackingUrl}>{order.shipment.trackingUrl}</a>
                        </div>
                      </div>
                      {isTrackingLoading ? (
                        <div className="flex items-center justify-center py-6 gap-2 text-xs font-semibold text-slate-500">
                          <Loader2 className="size-4 animate-spin text-[#e6202f]" />
                          Loading live tracking updates...
                        </div>
                      ) : (
                        <TrackingDetailsDisplay trackingData={trackingDetails} />
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">No shipment details available yet.</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === "inspections" && (
              <div className="space-y-4 animate-in fade-in duration-200">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <ShieldCheck className="size-3.5 text-[#e6202f]" /> Inspections
                    </h4>
                    <button
                      onClick={() => {
                        setShowInspectionForm(!showInspectionForm)
                        if (showInspectionForm) {
                          setInspectionMessageError("")
                        }
                      }}
                      className="flex items-center gap-1 text-[10px] font-bold text-[#e6202f] hover:text-[#be001e] transition-colors bg-[#e6202f]/5 hover:bg-[#e6202f]/10 border border-[#e6202f]/20 rounded-lg px-2.5 py-1 cursor-pointer"
                    >
                      <Plus className="size-3" /> Add Inspection
                    </button>
                  </div>
                  {showInspectionForm && (
                    <form onSubmit={handleInspectionSubmit} noValidate className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Inspection Note / Message</label>
                        <textarea
                          placeholder="e.g. Inspect the raw materials quality and dimensions"
                          value={inspectionMessage}
                          onChange={(e) => {
                            setInspectionMessage(e.target.value)
                            if (inspectionMessageError) setInspectionMessageError("")
                          }}
                          onBlur={(e) => validateInspectionMessage(e.target.value)}
                          className={`w-full bg-white border ${
                            inspectionMessageError ? "border-red-300 focus:border-red-500" : "border-slate-200 focus:border-[#be001e]"
                          } rounded-lg px-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none transition-colors min-h-[60px]`}
                        />
                        {inspectionMessageError && (
                          <p className="text-[10px] text-red-600 font-semibold">{inspectionMessageError}</p>
                        )}
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => {
                            setShowInspectionForm(false)
                            setInspectionMessageError("")
                          }}
                          className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button type="submit" disabled={addInspectionMutation.isPending} className="text-[10px] font-bold uppercase tracking-wider text-white bg-[#e6202f] hover:bg-[#be001e] px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 cursor-pointer">
                          {addInspectionMutation.isPending ? "Adding..." : "Add Inspection"}
                        </button>
                      </div>
                    </form>
                  )}
                  {(() => {
                    const inspections = order.inspections || (order.inspection ? [order.inspection] : [])
                    if (inspections.length === 0) return <p className="text-xs text-slate-400 italic">No inspections requested yet.</p>
                    return (
                      <div className="space-y-2">
                        {inspections.map((inspection: any) => (
                          <div key={inspection.id} className="flex flex-col gap-3 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-xl p-4 transition-colors text-xs">
                            <div className="flex justify-between items-start gap-3">
                              <div className="space-y-1 text-left flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-slate-700">Inspection ID:</span>
                                  <span className="font-mono text-slate-800 bg-slate-100 px-1 py-0.5 rounded text-[10px]">{inspection.id.slice(0, 8).toUpperCase()}</span>
                                </div>
                                {inspection.message && <p className="text-[11px] text-slate-600 font-semibold leading-relaxed">{inspection.message}</p>}
                                <span className="text-[9px] text-slate-400 block">Requested: {formatDate(inspection.createdAt)}</span>
                              </div>
                              <span className={`inline-flex items-center rounded px-2 py-0.5 text-[9px] font-bold border uppercase tracking-wider ${inspection.status === "COMPLETED" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : inspection.status === "IN_PROGRESS" ? "bg-sky-50 text-sky-700 border-sky-100" : inspection.status === "CANCELLED" ? "bg-red-50 text-red-700 border-red-100" : "bg-amber-50 text-amber-700 border-amber-100"}`}>
                                {inspection.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  })()}
                </div>
              </div>
            )}
            </div>
          </div>

          {/* Right: Timestamps */}
          <div className="lg:col-span-1 space-y-4 lg:border-l lg:border-[#be001e]/10 lg:pl-6 text-left">
            <div className="space-y-3">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                <Calendar className="size-3.5 text-[#e6202f]" /> Timestamps
              </h4>
              <div className="relative pl-5 space-y-4">
                <div className="absolute left-[7px] top-1.5 bottom-1.5 w-[2px] bg-[#be001e]/15"></div>
                <div className="relative flex gap-2.5 items-start">
                  <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-sky-100 bg-[#e0f2fe]">
                    <div className="size-1.5 rounded-full bg-sky-600"></div>
                  </div>
                  <div className="space-y-0.5 min-w-0">
                    <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">ORDER DATE</span>
                    <p className="text-xs font-bold text-slate-800 leading-none">{formatDate(order.createdAt)}</p>
                  </div>
                </div>
                {/* {q && (
                  <div className="relative flex gap-2.5 items-start">
                    <div className="absolute -left-[23px] top-0.5 flex items-center justify-center size-[18px] rounded-full border border-red-100 bg-[#fee2e2]">
                      <div className="size-1.5 rounded-full bg-red-600"></div>
                    </div>
                    <div className="space-y-0.5 min-w-0">
                      <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider">RFQ REFERENCE</span>
                      <p className="text-xs font-bold text-slate-800 leading-none font-mono">#{q.rfqId?.slice(0, 8).toUpperCase() ?? "N/A"}</p>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </div>
        </div>
      </div>
      {selectedVariantId && (
        <VariantDetailSidebar
          variantId={selectedVariantId}
          onClose={() => setSelectedVariantId(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedProductId && (
        <ProductDetailSidebar
          productId={selectedProductId}
          onClose={() => setSelectedProductId(null)}
          onCompanyClick={(c) => setSelectedCompany(c)}
        />
      )}
      {selectedCompany && (
        <CompanyDetailSidebar
          company={selectedCompany}
          onClose={() => setSelectedCompany(null)}
          level={selectedProductId || selectedVariantId ? 1 : 0}
        />
      )}
      {selectedExternalSeller && <ExternalSellerDetailSidebar externalSeller={selectedExternalSeller} onClose={() => setSelectedExternalSeller(null)} />}
      {selectedQuotation && <QuotationDetailSidebar quotation={selectedQuotation} onClose={() => setSelectedQuotation(null)} />}
    </div>
  )
}
